import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useCycleStatus, useStickers, type Sticker } from './useOwnership';
import { StickerCell } from './StickerCell';
import { AlbumTabs } from '../albums/AlbumTabs';

type Filter = 'all' | 'fehlt' | 'doppelt';

type Group = {
  key: string;
  title: string;
  sub: string;
  stickers: Sticker[];
};

function groupStickers(stickers: Sticker[]): Group[] {
  const foils: Sticker[] = [];
  const byTeam = new Map<string, Sticker[]>();
  for (const s of stickers) {
    if (s.kind !== 'team') {
      foils.push(s);
    } else if (s.team) {
      const list = byTeam.get(s.team) ?? [];
      list.push(s);
      byTeam.set(s.team, list);
    }
  }
  const groups: Group[] = [];
  if (foils.length > 0) {
    foils.sort((a, b) => a.position - b.position);
    const codes = foils.map((s) => s.code);
    groups.push({
      key: '__foils',
      title: '★ Foils',
      sub: `${codes[0]}–${codes[codes.length - 1] ?? codes[0]}`,
      stickers: foils,
    });
  }
  const teams = Array.from(byTeam.keys()).sort();
  for (const team of teams) {
    const list = byTeam.get(team)!;
    list.sort((a, b) => a.position - b.position);
    groups.push({
      key: team,
      title: team,
      sub: `${list[0]?.code}–${list[list.length - 1]?.code}`,
      stickers: list,
    });
  }
  return groups;
}

export default function StickerGridPage() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <Navigate to="/albums" replace />;

  const { data: stickers, isLoading, error } = useStickers(slug);
  const cycle = useCycleStatus(slug);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const stats = useMemo(() => {
    if (!stickers) return { have: 0, doubles: 0, total: 0 };
    return {
      total: stickers.length,
      have: stickers.filter((s) => s.count >= 1).length,
      doubles: stickers.filter((s) => s.count >= 2).length,
    };
  }, [stickers]);

  const filtered = useMemo(() => {
    if (!stickers) return [];
    return stickers.filter((s) => {
      if (filter === 'fehlt' && s.count !== 0) return false;
      if (filter === 'doppelt' && s.count < 2) return false;
      if (search && !s.code.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [stickers, filter, search]);

  const groups = useMemo(() => groupStickers(filtered), [filtered]);

  return (
    <div className="min-h-full safe-bottom">
      <AlbumTabs slug={slug} active="stickers" />

      {/* Score-Header — die große Zahl im Visual System */}
      <div className="px-4 pt-2 pb-3 border-b border-border/60">
        <div className="flex items-baseline gap-1.5 text-text font-mono">
          <span className="text-[26px] font-bold leading-none">{stats.have}</span>
          <span className="text-[14px] text-muted/70">/ {stats.total}</span>
          <span className="text-[10px] font-semibold tracking-[.1em] text-muted ml-1">GEKLEBT</span>
          <span className="ml-auto text-[13px] font-bold text-status-double">
            {stats.doubles} doppelt
          </span>
        </div>
        <div className="mt-2 h-[7px] rounded-[4px] bg-surface2 overflow-hidden">
          <div
            className="h-full rounded-[4px] bg-primary transition-[width] duration-300"
            style={{ width: stats.total ? `${(stats.have / stats.total) * 100}%` : '0%' }}
          />
        </div>

        {/* Filter-Pills */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code suchen"
            className="flex-1 min-w-[140px] px-3 py-2 rounded-[10px] bg-surface text-text text-sm outline-none focus:ring-2 focus:ring-primary placeholder:text-muted"
          />
          <FilterPill current={filter} value="all" label="Alle" onClick={setFilter} />
          <FilterPill current={filter} value="fehlt" label={`Fehlt · ${stats.total - stats.have}`} onClick={setFilter} />
          <FilterPill current={filter} value="doppelt" label={`Doppelt · ${stats.doubles}`} onClick={setFilter} />
        </div>
      </div>

      {isLoading && <p className="p-6 text-muted">Lade Sticker…</p>}
      {error && <p className="p-6 text-status-missing">Konnte Sticker nicht laden</p>}

      <div className="px-3 pb-4">
        {groups.map((g) => (
          <div key={g.key}>
            <div className="sticky top-0 z-[1] bg-bg pt-3.5 pb-2 px-1 flex items-center gap-2">
              <span className="font-sans font-extrabold text-[13px] text-text">{g.title}</span>
              <span className="flex-1 h-px bg-surface2" />
              <span className="font-mono text-[10px] text-muted/80">{g.sub}</span>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
              {g.stickers.map((s) => (
                <StickerCell key={s.id} sticker={s} onTap={() => cycle(s.code, s.count)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {!isLoading && groups.length === 0 && (
        <EmptyState search={search} filter={filter} />
      )}
    </div>
  );
}

function FilterPill({
  current,
  value,
  label,
  onClick,
}: {
  current: Filter;
  value: Filter;
  label: string;
  onClick: (f: Filter) => void;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-3 py-1.5 rounded-full font-mono text-[11px] font-bold whitespace-nowrap transition-colors ${
        active ? 'bg-primary text-white border-[1.5px] border-primary' : 'bg-transparent border-[1.5px] border-surface2 text-muted'
      }`}
    >
      {label}
    </button>
  );
}

function EmptyState({ search, filter }: { search: string; filter: Filter }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="flex justify-center gap-1.5 mb-3">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-[22px] h-[22px] rounded-md border-2 border-dashed"
            style={{ borderColor: 'var(--cell-missing-border)' }}
          />
        ))}
      </div>
      <p className="font-sans font-extrabold text-text">
        {search ? 'Kein Treffer' : filter === 'fehlt' ? 'Album voll!' : 'Keine Doppelten'}
      </p>
      <p className="text-sm text-muted mt-1">
        {search ? 'Probier einen anderen Code' : 'Tipp den ersten Sticker an — Klack.'}
      </p>
    </div>
  );
}
